/**
 * What the formatting toolbar does to the text in the box. Pure functions
 * over (text, selection) so they can be tested without a textarea, and so
 * the three places the editor appears (Compose, Reply, Copy Library) cannot
 * drift apart in how a click edits the words.
 *
 * The marks themselves are defined once, in the shared mail-send block
 * (src/features/gmail/mailSend.ts): **bold**, _italic_, __underline__,
 * [words](url), "- " bullets. This file only knows how to type them.
 */

export interface Edit {
  text: string;
  /** Where the caret / selection lands afterwards. */
  start: number;
  end: number;
}

export type InlineMark = 'bold' | 'italic' | 'underline';

const MARKS: Record<InlineMark, string> = { bold: '**', italic: '_', underline: '__' };

/**
 * Wrap the selection in a mark. With nothing selected, drop in an empty pair
 * and leave the caret between them so the person just types. Clicking the
 * same mark on text already wrapped in it takes the mark off again.
 */
export function toggleInlineMark(text: string, start: number, end: number, mark: InlineMark): Edit {
  const m = MARKS[mark];
  const before = text.slice(0, start);
  const selected = text.slice(start, end);
  const after = text.slice(end);

  // Already wrapped, either inside or outside the selection: unwrap.
  if (selected.startsWith(m) && selected.endsWith(m) && selected.length >= m.length * 2) {
    const inner = selected.slice(m.length, selected.length - m.length);
    return { text: before + inner + after, start, end: start + inner.length };
  }
  if (before.endsWith(m) && after.startsWith(m)) {
    return {
      text: before.slice(0, -m.length) + selected + after.slice(m.length),
      start: start - m.length,
      end: end - m.length,
    };
  }

  if (selected === '') {
    return { text: before + m + m + after, start: start + m.length, end: start + m.length };
  }
  // Marks hug the words: a selection that grabbed a trailing space keeps the
  // space outside, or the mark would not render.
  const lead = selected.length - selected.trimStart().length;
  const trail = selected.length - selected.trimEnd().length;
  const core = selected.trim();
  if (core === '') {
    return { text, start, end };
  }
  const wrapped = selected.slice(0, lead) + m + core + m + selected.slice(selected.length - trail);
  return {
    text: before + wrapped + after,
    start: start + lead,
    end: start + lead + core.length + m.length * 2,
  };
}

/**
 * A link on the selected words. With nothing selected, a link with the words
 * left for the person to fill in. Either way the caret lands on the address,
 * because that is the part nobody can guess.
 */
export function insertLink(text: string, start: number, end: number): Edit {
  const selected = text.slice(start, end);
  const words = selected.trim() === '' ? 'words' : selected;
  const url = 'https://';
  const piece = `[${words}](${url})`;
  const urlStart = start + words.length + 3; // "[" + words + "](" → the address starts here
  return {
    text: text.slice(0, start) + piece + text.slice(end),
    start: urlStart,
    end: urlStart + url.length,
  };
}

/**
 * Bullets on every line the selection touches; off again if they all already
 * have one. Works on whole lines because a bullet is a line's property.
 */
export function toggleBullets(text: string, start: number, end: number): Edit {
  const lineStart = text.lastIndexOf('\n', start - 1) + 1;
  const lineEndIndex = text.indexOf('\n', Math.max(end - 1, start));
  const lineEnd = lineEndIndex === -1 ? text.length : lineEndIndex;
  const block = text.slice(lineStart, lineEnd);
  const lines = block.split('\n');
  const allBulleted = lines.every((line) => /^\s*[-*•]\s+/.test(line) || line.trim() === '');
  const next = lines
    .map((line) => {
      if (line.trim() === '') return line;
      return allBulleted ? line.replace(/^(\s*)[-*•]\s+/, '$1') : `- ${line}`;
    })
    .join('\n');
  const edited = text.slice(0, lineStart) + next + text.slice(lineEnd);
  return { text: edited, start: lineStart, end: lineStart + next.length };
}

/** Drop a token (or any snippet) at the caret, replacing the selection. */
export function insertAt(text: string, start: number, end: number, snippet: string): Edit {
  const caret = start + snippet.length;
  return { text: text.slice(0, start) + snippet + text.slice(end), start: caret, end: caret };
}
